import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import { LdpmHistoryPoint, LdpmMetricKey } from '../types/StockData';

interface LdpmHistoryChartProps {
  data: LdpmHistoryPoint[];
  selectedMetric: LdpmMetricKey;
  onMetricChange: (metric: LdpmMetricKey) => void;
}

interface ChartDataPoint {
  timestamp: string;
  time: string;
  date: string;
  ldpm: number;
  currentClose: number;
  volume: number;
  distanceFromPrice: number;
  exponentialMultiplier: number;
  priceRange: number;
  priceDifference: number;
  liquidityAssessment: string;
  isLiquidityGood: boolean;
  metricValue: number;
  metricName: string;
  zScore?: number;
  isAnomaly?: boolean;
}

const LdpmHistoryChart: React.FC<LdpmHistoryChartProps> = ({ 
  data, 
  selectedMetric,
  onMetricChange 
}) => {

  const chartData: ChartDataPoint[] = data.map(point => {
    const utcTimestamp = point.timestamp.endsWith('Z') ? point.timestamp : point.timestamp + 'Z';
    const utcDate = new Date(utcTimestamp);
    
    const estTime = utcDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/New_York'
    });

    const estDate = utcDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York'
    });

    return {
      timestamp: point.timestamp,
      time: estTime,
      date: estDate,
      ldpm: point.ldpm,
      currentClose: point.currentClose,
      volume: point.volume,
      distanceFromPrice: point.distanceFromPrice,
      exponentialMultiplier: point.exponentialMultiplier,
      priceRange: point.priceRange,
      priceDifference: point.priceDifference,
      liquidityAssessment: point.liquidityAssessment,
      isLiquidityGood: point.isLiquidityGood,
      metricValue: point[selectedMetric] as number,
      metricName: selectedMetric
    };
  });

  // Rolling z-score for LDPM (no look-ahead)
  const ldpmSeries = chartData.map(d => d.ldpm);
  const windowN = 20;
  const zThresh = 3.0;
  const zScores: (number | null)[] = new Array(ldpmSeries.length).fill(null);
  for (let i = 0; i < ldpmSeries.length; i++) {
    const start = Math.max(0, i - windowN);
    const slice = ldpmSeries.slice(start, i); // use prior values only
    if (slice.length < Math.min(windowN, 10)) {
      zScores[i] = null;
      continue;
    }
    const mean = slice.reduce((s, v) => s + v, 0) / slice.length;
    const variance = slice.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / slice.length;
    const std = Math.sqrt(variance);
    if (!isFinite(std) || std === 0) {
      zScores[i] = null;
      continue;
    }
    zScores[i] = (ldpmSeries[i] - mean) / std;
  }

  // Attach z-scores and anomaly flags to chart data
  const chartDataWithZ: ChartDataPoint[] = chartData.map((d, i) => ({
    ...d,
    zScore: zScores[i] ?? undefined,
    isAnomaly: zScores[i] != null ? Math.abs(zScores[i] as number) >= zThresh : false,
  }));

  // Percentile utilities
  const percentile = (sorted: number[], p: number): number => {
    if (sorted.length === 0) return 0;
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx);
    const hi = Math.ceil(idx);
    if (lo === hi) return sorted[lo];
    const w = idx - lo;
    return sorted[lo] * (1 - w) + sorted[hi] * w;
  };
  const percentileRank = (sorted: number[], value: number): number => {
    if (sorted.length === 0) return 0;
    // binary search upper bound
    let lo = 0, hi = sorted.length;
    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (sorted[mid] <= value) lo = mid + 1; else hi = mid;
    }
    return (lo / sorted.length) * 100;
  };

  const sortedLdpmAll = [...ldpmSeries].filter(v => isFinite(v)).sort((a, b) => a - b);
  const p20 = percentile(sortedLdpmAll, 0.20);
  const p50 = percentile(sortedLdpmAll, 0.50);
  const p80 = percentile(sortedLdpmAll, 0.80);
  const lastLdpm = ldpmSeries[ldpmSeries.length - 1];
  const lastZ = zScores[zScores.length - 1];
  const lastPct = percentileRank(sortedLdpmAll, lastLdpm);

  const zBadgeClass = lastZ == null
    ? 'text-gray-700 bg-gray-100 border-gray-200'
    : (Math.abs(lastZ) >= zThresh
        ? 'text-red-700 bg-red-50 border-red-200'
        : 'text-blue-700 bg-blue-50 border-blue-200');
  const pctBadgeClass = lastPct >= 80
    ? 'text-green-700 bg-green-50 border-green-200'
    : lastPct <= 20
      ? 'text-red-700 bg-red-50 border-red-200'
      : 'text-gray-700 bg-gray-50 border-gray-200';
  // Calculate domains for proper scaling
  const priceValues = chartData.map(d => d.currentClose).filter(v => Number.isFinite(v));
  const metricValues = chartData.map(d => d.metricValue);
  
  // Robust price domain with padding even for single-value series
  const minPrice = priceValues.length ? Math.min(...priceValues) : 0;
  const maxPrice = priceValues.length ? Math.max(...priceValues) : 1;
  const priceRange = Math.max(0, maxPrice - minPrice);
  const base = maxPrice !== 0 ? Math.abs(maxPrice) : 1;
  const pricePadding = priceRange > 0 ? priceRange * 0.05 : base * 0.02; // 5% if range, else 2% of magnitude
  const priceDomain: [number, number] = [
    minPrice - pricePadding,
    maxPrice + pricePadding
  ];
  
  const metricDomain = [
    Math.min(...metricValues) * 0.95,
    Math.max(...metricValues) * 1.05
  ];

  // LDPM axis domain with padding to ensure consistent, readable scaling
  const ldpmValues = chartDataWithZ.map(d => d.ldpm);
  const minLdpm = Math.min(...ldpmValues);
  const maxLdpm = Math.max(...ldpmValues);
  const ldpmRange = Math.max(0, maxLdpm - minLdpm);
  const ldpmPadding = (ldpmRange || Math.abs(maxLdpm || 1) * 0.05) * 0.05; // small fallback padding
  const ldpmDomain: [number, number] = [
    minLdpm - ldpmPadding,
    maxLdpm + ldpmPadding
  ];
  const yRightDomain = selectedMetric === 'ldpm' ? ldpmDomain : metricDomain;

  // When LDPM is selected, scale it to the price axis domain for shape overlay
  const mapLdpmToPrice = (v: number) => {
    const denom = (maxLdpm - minLdpm) || 1e-9;
    return priceDomain[0] + ((v - minLdpm) / denom) * (priceDomain[1] - priceDomain[0]);
  };
  const chartDataForChart = selectedMetric === 'ldpm'
    ? chartDataWithZ.map(d => ({ ...d, ldpmOverlay: mapLdpmToPrice(d.ldpm) }))
    : chartDataWithZ;
  const p20Scaled = mapLdpmToPrice(p20);
  const p50Scaled = mapLdpmToPrice(p50);
  const p80Scaled = mapLdpmToPrice(p80);

  const formatMetricValue = (value: number, metric: LdpmMetricKey) => {
    if (metric === 'volume') {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
      return value.toFixed(0);
    }
    return value.toFixed(2);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-2xl border border-gray-200">
          <p className="font-semibold text-gray-800 mb-2">{`${data.date} ${data.time}`}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm">Price: <span className="font-semibold">${data.currentClose.toFixed(2)}</span></span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">LDPM: <span className="font-semibold">{data.ldpm.toFixed(2)}</span></span>
            </div>
            {selectedMetric !== 'ldpm' && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm">{selectedMetric}: <span className="font-semibold">{normMode === 'pct' ? `${Number(data.metricDisplay).toFixed(2)}%` : formatMetricValue(Number(data.metricDisplay), selectedMetric)}</span></span>
              </div>
            )}
            <div className={`mt-2 px-2 py-1 rounded text-xs font-medium ${
              data.isLiquidityGood 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {data.liquidityAssessment}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Correlation and normalization (for non-LDPM metric)
  type NormMode = 'raw' | 'pct';
  const [normMode, setNormMode] = useState<NormMode>('raw');

  const computeReturns = (series: number[]): number[] => {
    const out: number[] = [];
    for (let i = 1; i < series.length; i++) {
      const prev = series[i - 1];
      const curr = series[i];
      if (!isFinite(prev) || prev === 0 || !isFinite(curr)) continue;
      out.push((curr - prev) / Math.abs(prev));
    }
    return out;
  };

  const pearson = (a: number[], b: number[]): number | null => {
    const n = Math.min(a.length, b.length);
    if (n < 3) return null;
    const ax = a.slice(-n);
    const bx = b.slice(-n);
    const mean = (xs: number[]) => xs.reduce((s, v) => s + v, 0) / xs.length;
    const ma = mean(ax), mb = mean(bx);
    let num = 0, da = 0, db = 0;
    for (let i = 0; i < n; i++) {
      const va = ax[i] - ma;
      const vb = bx[i] - mb;
      num += va * vb; da += va * va; db += vb * vb;
    }
    const denom = Math.sqrt(da * db);
    if (denom === 0) return null;
    return num / denom;
  };

  const priceSeriesFull: number[] = chartData.map(d => d.currentClose);
  const baseMetricSeriesFull: number[] = (selectedMetric === 'ldpm')
    ? chartData.map(d => d.ldpm)
    : chartData.map(d => d.metricValue);
  const corr = pearson(computeReturns(priceSeriesFull), computeReturns(baseMetricSeriesFull));
  const corrText = corr == null ? 'N/A' : (corr >= 0 ? `+${corr.toFixed(2)}` : corr.toFixed(2));
  const corrClass = corr == null ? 'text-gray-700 bg-gray-100 border-gray-200' : (corr >= 0 ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200');

  // Rolling correlation sparkline data (20)
  const rp = (() => {
    const out: (number | null)[] = new Array(priceSeriesFull.length).fill(null);
    for (let i = 1; i < priceSeriesFull.length; i++) {
      const prev = priceSeriesFull[i - 1];
      const curr = priceSeriesFull[i];
      if (isFinite(prev) && prev !== 0 && isFinite(curr)) out[i] = (curr - prev) / Math.abs(prev); else out[i] = null;
    }
    return out;
  })();

  const rm = (() => {
    const out: (number | null)[] = new Array(baseMetricSeriesFull.length).fill(null);
    for (let i = 1; i < baseMetricSeriesFull.length; i++) {
      const prev = baseMetricSeriesFull[i - 1];
      const curr = baseMetricSeriesFull[i];
      if (isFinite(prev) && prev !== 0 && isFinite(curr)) out[i] = (curr - prev) / Math.abs(prev); else out[i] = null;
    }
    return out;
  })();

  const rollingWindowRC = 20;
  const rollingCorr: (number | null)[] = new Array(chartData.length).fill(null);
  for (let i = 0; i < chartData.length; i++) {
    const start = Math.max(1, i - rollingWindowRC + 1);
    const a: number[] = [];
    const b: number[] = [];
    for (let j = start; j <= i; j++) {
      if (rp[j] != null && rm[j] != null) { a.push(rp[j] as number); b.push(rm[j] as number); }
    }
    rollingCorr[i] = a.length >= 3 ? pearson(a, b) : null;
  }
  const rollingSparkData = chartData.map((d, i) => ({ date: `${d.date} ${d.time}`, rc: rollingCorr[i] }));
  const lastRcVal: number | null = (() => {
    for (let i = rollingCorr.length - 1; i >= 0; i--) { const v = rollingCorr[i]; if (v != null && isFinite(v)) return v as number; }
    return null;
  })();
  const rollingColor = lastRcVal == null ? '#9ca3af' : (lastRcVal >= 0 ? '#16a34a' : '#ef4444');

  // Normalization mapping for non-LDPM metric
  const needsRightAxis = selectedMetric !== 'ldpm';
  const baseVal = (() => {
    if (!needsRightAxis || normMode === 'raw') return 1;
    const vals = chartData.map(d => d.metricValue).filter((v) => isFinite(v));
    const nz = vals.find((v) => v !== 0);
    return (nz ?? vals[0] ?? 1) || 1;
  })();
  const chartDataDisplay = needsRightAxis && normMode === 'pct'
    ? chartDataForChart.map(d => ({ ...d, metricDisplay: (((d.metricValue / baseVal) - 1) * 100) }))
    : chartDataForChart.map(d => ({ ...d, metricDisplay: d.metricValue }));

  const rightAxisTick = needsRightAxis && normMode === 'pct'
    ? (value: number) => `${value.toFixed(2)}%`
    : (value: number) => value.toFixed(1);

  const rightAxisDomain: [number, number] = (() => {
    if (!needsRightAxis) return yRightDomain as [number, number];
    if (normMode === 'pct') {
      const vals = chartDataDisplay.map(d => d.metricDisplay as number).filter((v) => isFinite(v));
      if (vals.length === 0) return [0, 1];
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const pad = (max - min) * 0.1 || Math.abs(max || 1) * 0.02;
      return [min - pad, max + pad];
    }
    return yRightDomain as [number, number];
  })();

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-xl border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 tracking-tight">LDPM History</h2>
        <div className="flex items-center gap-3">
          {/* Correlation badge and sparkline */}
          <span className={`px-2 py-1 text-xs font-semibold rounded-lg border ${corrClass}`} title="Correlation of price vs metric returns">
            Corr: {corrText}
          </span>
          <div className="hidden sm:flex items-center gap-2" title="Rolling correlation (20)">
            <span className="text-[10px] text-gray-500">20d</span>
            <div className="w-32 h-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rollingSparkData} margin={{ top: 2, right: 6, bottom: 2, left: 6 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis domain={[-1, 1]} hide />
                  <Line type="monotone" dataKey="rc" stroke={rollingColor} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {selectedMetric === 'ldpm' && (
            <>
              <span className={`px-2 py-1 text-xs font-semibold rounded-lg border ${zBadgeClass}`} title="Rolling z-score (20)">
                Z20: {lastZ == null ? 'N/A' : (lastZ >= 0 ? `+${lastZ.toFixed(2)}` : lastZ.toFixed(2))}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-lg border ${pctBadgeClass}`} title="Percentile of current LDPM vs window">
                Pctl: {Number.isFinite(lastPct) ? `${lastPct.toFixed(0)}th` : 'N/A'}
              </span>
            </>
          )}
          {selectedMetric !== 'ldpm' && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <span className="text-xs">Normalize:</span>
              <select
                value={normMode}
                onChange={(e) => setNormMode(e.target.value as NormMode)}
                className="px-2 py-1 text-xs font-medium border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="raw">Raw</option>
                <option value="pct">% vs baseline</option>
              </select>
            </label>
          )}
        </div>
      </div>

      <div style={{ width: '100%', height: '600px' }}>
        <ResponsiveContainer>
          <LineChart
            data={chartDataDisplay}
            margin={{ top: 20, right: 80, left: 60, bottom: 20 }}
          >
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="ldpmGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="metricGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="2 4" stroke="#e5e7eb" opacity={0.6} />

            {/* Percentile band (20th to 80th) and median line (only when LDPM is selected) */}
            {selectedMetric === 'ldpm' && (
              <>
                <ReferenceArea
                  y1={p20Scaled}
                  y2={p80Scaled}
                  x1={chartDataForChart[0]?.time}
                  x2={chartDataForChart[chartDataForChart.length - 1]?.time}
                  yAxisId="price"
                  fill="#d1fae5"
                  fillOpacity={0.35}
                  stroke="#10b981"
                  strokeOpacity={0.25}
                />
                <ReferenceLine y={p50Scaled} yAxisId="price" stroke="#10b981" strokeDasharray="4 4" label={{ value: 'Median', position: 'insideRight', fill: '#10b981', fontSize: 10 }} />
              </>
            )}
            
            <XAxis 
              dataKey="time"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#9ca3af' }}
              axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            
            <YAxis 
              yAxisId="price"
              orientation="left"
              tick={{ fontSize: 12, fill: '#3b82f6', fontWeight: 600 }}
              tickLine={{ stroke: '#3b82f6' }}
              axisLine={{ stroke: '#3b82f6', strokeWidth: 2 }}
              domain={priceDomain}
              tickFormatter={(value) => `$${value.toFixed(2)}`}
            />
            
            {selectedMetric !== 'ldpm' && (
              <YAxis 
                yAxisId="ldpm"
                orientation="right"
                tick={{ fontSize: 12, fill: '#10b981', fontWeight: 600 }}
                tickLine={{ stroke: '#10b981' }}
                axisLine={{ stroke: '#10b981', strokeWidth: 2 }}
                domain={rightAxisDomain}
                tickFormatter={rightAxisTick}
              />
            )}
            
            <Tooltip content={<CustomTooltip />} />
            
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="currentClose"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#ffffff' }}
              fill="url(#priceGradient)"
              name="Price"
            />
            
            {selectedMetric === 'ldpm' && (
              <Line
                yAxisId="price"
                type="monotone"
                dataKey="ldpmOverlay"
                stroke="#10b981"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#ffffff' }}
                fill="url(#ldpmGradient)"
                name="LDPM"
              />
            )}

            {/* Anomaly markers on LDPM based on z-score (only when LDPM selected) */}
            {selectedMetric === 'ldpm' && chartDataForChart.map((d: any, i: number) => (
              d.isAnomaly ? (
                <ReferenceDot
                  key={`anom-${i}`}
                  x={d.time}
                  y={d.ldpmOverlay}
                  yAxisId="price"
                  r={4}
                  fill="#ef4444"
                  stroke="#ffffff"
                  strokeWidth={1}
                />
              ) : null
            ))}
            
            {selectedMetric !== 'ldpm' && (
              <Line
                yAxisId="ldpm"
                type="monotone"
                dataKey="metricDisplay"
                stroke="#8b5cf6"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 4, fill: '#8b5cf6', strokeWidth: 2, stroke: '#ffffff' }}
                fill="url(#metricGradient)"
                name={selectedMetric}
              />
            )}

          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LdpmHistoryChart;
